<script setup lang="ts" generic="T extends { id?: number | string }">
  import { computed } from 'vue';
  import CustomCheckbox from '@/shared/ui/checkboxes/CustomCheckbox.vue';

  const props = withDefaults(
    defineProps<{
      title: string;
      hideSelection?: boolean;
      selectAllLabel: string;
      items: T[];
      selectedItems: string[];
      selectionClass?: string;
      /** When true, the list box grows with its content (capped at the usual max height) instead
       * of always reserving a fixed height — for sections whose item count varies a lot. */
      fitContent?: boolean;
      /** When true, the select-all row's space collapses entirely while hideSelection is true,
       * instead of staying reserved (empty) — only safe for callers that don't rely on that
       * spacer to align with a sibling SelectionSection rendered side by side. */
      collapseWhenHidden?: boolean;
      radiusClass?: string;
      paddingClass?: string;
      /** Overrides the list box height, for callers that must fit inside a bounded container
       * (a modal, for instance) instead of always reserving the default height. */
      heightClass?: string;
      countTextClass?: string;
      count: number;
      countLabel: string;
      getItemId?: (item: T) => string;
    }>(),
    {
      collapseWhenHidden: false,
      radiusClass: 'rounded-lg',
      paddingClass: 'p-6',
      countTextClass: 'mt-3 text-sm text-gray-03 text-left',
    }
  );

  const emit = defineEmits<{
    (e: 'update:selectedItems', value: string[]): void;
  }>();

  const resolveItemId = (item: T): string =>
    props.getItemId ? props.getItemId(item) : (item.id?.toString() ?? '');

  const allItemIds = computed(() => props.items.map(resolveItemId).filter((id) => id !== ''));

  const allSelected = computed(
    () =>
      allItemIds.value.length > 0 &&
      allItemIds.value.every((id) => props.selectedItems.includes(id))
  );

  const handleSelectAll = (event: Event) => {
    const { checked } = event.target as HTMLInputElement;
    emit('update:selectedItems', checked ? allItemIds.value : []);
  };
</script>

<template>
  <div class="w-full min-w-0">
    <div
      v-if="!(hideSelection && collapseWhenHidden)"
      class="flex min-h-12 items-center gap-3 p-3"
    >
      <template v-if="!hideSelection">
        <CustomCheckbox
          :id="`select-all-${title.toLowerCase()}`"
          :checked="allSelected"
          :disabled="allItemIds.length === 0"
          @change="handleSelectAll"
        />
        <p class="text-sm/5 font-medium">
          {{ selectAllLabel }}
        </p>
      </template>
    </div>
    <div
      :class="[
        selectionClass,
        radiusClass,
        paddingClass,
        heightClass ?? (fitContent ? 'max-h-108' : 'h-108'),
      ]"
      class="border border-border-color text-left text-sm overflow-y-auto"
    >
      <table class="w-full border-collapse">
        <thead>
          <tr>
            <slot name="columns" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in items"
            :key="item.id"
          >
            <slot
              name="item"
              :item="item"
            />
          </tr>
        </tbody>
      </table>
    </div>
    <div :class="countTextClass">
      {{ countLabel }} {{ count }}
    </div>
  </div>
</template>
